import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Check, ChevronsUpDown, Plus, Building2, Loader2, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu'
import { useBrand } from '@/context/brandContext'
import { trpc } from '@/config/trpc'
import { UpgradeModal } from '@/components/upgrade-modal'

export function BrandSwitcher() {
  const navigate = useNavigate()
  const { brands, currentBrand, switchBrand, isLoading, isSwitching, switchingToBrandId } =
    useBrand()
  const { data: usage } = trpc.usageLimit.get.useQuery()

  const [isOpen, setIsOpen] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  // -1 is unlimited. Until usage loads, let the click through: the backend
  // entity limit still guards creation and /brand/new opens the same modal.
  const brandMax = usage?.brands.max ?? -1
  const atBrandLimit = brandMax !== -1 && brands.length >= brandMax
  const visibleBrand = brands.find((brand) => brand.id === switchingToBrandId) ?? currentBrand

  const handleSwitch = async (brandId: number) => {
    if (brandId === currentBrand?.id) {
      setIsOpen(false)
      return
    }
    await switchBrand(brandId)
    setIsOpen(false)
  }

  const handleOpenChange = (open: boolean) => {
    // Don't allow closing while switching
    if (isSwitching && !open) return
    setIsOpen(open)
  }

  if (isLoading) {
    return <div className="bg-muted h-9 w-full animate-pulse rounded-md" />
  }

  // Don't show if user has no brands (shouldn't happen in normal flow)
  if (brands.length === 0) {
    return null
  }

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="bg-card w-full justify-between gap-2"
            disabled={isSwitching}>
            <div className="flex min-w-0 items-center gap-2">
              <Building2 className="h-4 w-4 shrink-0" />
              <span className="truncate">{visibleBrand?.name ?? 'Select Brand'}</span>
            </div>
            {isSwitching ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin opacity-70" />
            ) : (
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-56" align="start">
          <DropdownMenuLabel>Brands</DropdownMenuLabel>
          <DropdownMenuSeparator />

          {brands.map((brand) => {
            const isCurrent = currentBrand?.id === brand.id
            const isSwitchingTo = switchingToBrandId === brand.id

            return (
              <DropdownMenuItem
                key={brand.id}
                onClick={(e) => {
                  e.preventDefault()
                  handleSwitch(brand.id)
                }}
                disabled={isSwitching}
                className="cursor-pointer">
                <Building2 className="mr-2 h-4 w-4" />
                <span className="flex-1 truncate">{brand.name}</span>
                {isSwitchingTo ? (
                  <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                ) : (
                  isCurrent && <Check className="ml-2 h-4 w-4" />
                )}
              </DropdownMenuItem>
            )
          })}

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={() => {
              setIsOpen(false)
              if (atBrandLimit) {
                setShowUpgradeModal(true)
                return
              }
              navigate('/brand/new')
            }}
            disabled={isSwitching}
            className="cursor-pointer">
            <Plus className="mr-2 h-4 w-4" />
            <span>Create Brand</span>
            {atBrandLimit && <Sparkles className="ml-auto h-3.5 w-3.5 text-amber-500" />}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        limitType="brands"
        surface="brand_switcher"
      />
    </>
  )
}
